import mixpanel from 'mixpanel-browser'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { MixpanelConfig, TrackingEvent, UserData } from './types'

class AnalyticsService {
  private config: MixpanelConfig | null = null
  private isInitialized = false
  private timingEvents: Map<string, number> = new Map()
  private storedDeviceId: string | null = null

  /**
   * Store device ID from main platform before Mixpanel initialization.
   * This device ID will be used for identity linking during alias.
   *
   * @param deviceId - Device ID from main platform
   */
  setDeviceIdBeforeInit(deviceId: string): void {
    this.storedDeviceId = deviceId
    if (this.config?.debug) {
      console.log('📱 Stored device ID from main platform:', deviceId)
    }
  }

  /**
   * Get stored device ID from main platform.
   *
   * @returns Stored device ID or null
   */
  getStoredDeviceId(): string | null {
    return this.storedDeviceId
  }

  /**
   * Initialize Mixpanel with configuration
   */
  async init(config: MixpanelConfig): Promise<void> {
    this.config = config

    if (!config.enabled) {
      console.log('Mixpanel disabled via configuration')
      return
    }

    try {
      // CRITICAL: Check if Mixpanel localStorage has a distinct_id that looks like a user ID
      // (not a device ID). If the main app called reset(), it cleared the cookie, but the auth
      // platform's localStorage might still have the old user ID. We need to clear it before init.
      // BUT: Only clear if there's NO auth session - if user is still authenticated on auth platform,
      // we should keep their Mixpanel identity.
      const mpLocalStorageKey = `mp_${config.token}_mixpanel`
      const storedDistinctId =
        JSON.parse(localStorage.getItem(mpLocalStorageKey) || '{}')
          ?.distinct_id || null

      // Check if user has an active auth session (similar to useAuth.ts)
      let hasAuthSession = false
      try {
        const session = await fetchAuthSession({ forceRefresh: false })
        hasAuthSession = !!session?.tokens?.idToken?.toString()
      } catch {
        // If fetchAuthSession fails, user is not authenticated
        hasAuthSession = false
      }

      // Only clear Mixpanel localStorage if:
      // 1. localStorage has a user ID (not a device ID)
      // 2. AND there's NO auth session (user logged out on both platforms)
      if (
        storedDistinctId &&
        !storedDistinctId.startsWith('$device:') &&
        !hasAuthSession
      ) {
        // This looks like a stale user ID from a previous authenticated session
        // Since there's no auth session, the user logged out, so we should clear it
        console.log(
          '[MIXPANEL] Clearing stale user ID from localStorage (no auth session):',
          {
            oldDistinctId: storedDistinctId,
            hasAuthSession,
            note: 'User logged out - cookie is source of truth, will generate new anonymous ID',
          }
        )
        // Clear the localStorage key directly to remove stale user ID
        localStorage.removeItem(mpLocalStorageKey)
        // Also clear any other Mixpanel localStorage keys that might have user data
        // Mixpanel stores data with keys like: mp_<token>_*, mp_<token>_super_properties, etc.
        const mixpanelKeys: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key?.startsWith(`mp_${config.token}_`)) {
            mixpanelKeys.push(key)
          }
        }
        mixpanelKeys.forEach((key) => localStorage.removeItem(key))
        console.log(
          '[MIXPANEL] Cleared Mixpanel localStorage keys:',
          mixpanelKeys
        )
      } else if (
        storedDistinctId &&
        !storedDistinctId.startsWith('$device:') &&
        hasAuthSession
      ) {
        // User has auth session, so keep their Mixpanel identity
        console.log(
          '[MIXPANEL] Keeping user ID in localStorage (user is authenticated):',
          {
            storedDistinctId,
            hasAuthSession,
            note: 'User is authenticated on auth platform, keeping Mixpanel identity',
          }
        )
      }

      mixpanel.init(config.token, {
        debug: config.debug || false,
        track_pageview: config.track_pageview ?? true,
        persistence: 'localStorage',
        api_host: config.apiHost,
        batch_requests: false, // Send immediately for debugging
        cross_subdomain_cookie: true, // Enable identity persistence across subdomains
        secure_cookie: true, // Use secure cookies for HTTPS
      })

      // If a bridge ID was provided from the main platform, adopt it immediately.
      // This applies to both device IDs ($device:...) and user IDs.
      if (this.storedDeviceId) {
        const currentDistinctId = mixpanel.get_distinct_id()
        if (currentDistinctId !== this.storedDeviceId) {
          mixpanel.identify(this.storedDeviceId)
        }
      }

      // Note: With cross_subdomain_cookie: true, Mixpanel may sync distinct_id
      // across subdomains via cookies, but we still force bridge consistency here.
      this.isInitialized = true

      if (config.debug) {
        const currentDistinctId = mixpanel.get_distinct_id()
        console.log('✅ Mixpanel initialized successfully:', {
          token: config.token,
          distinctId: currentDistinctId,
          storedDeviceId: this.storedDeviceId,
          usedStoredDeviceId:
            this.storedDeviceId && currentDistinctId === this.storedDeviceId,
        })
      }
    } catch (error) {
      console.error('❌ Failed to initialize Mixpanel:', error)
    }
  }

  /**
   * Track an event
   */
  track(event: TrackingEvent): void {
    if (!this.isReady()) return

    try {
      const sanitizedProperties = this.sanitizeProperties({
        ...event.properties,
        timestamp: new Date().toISOString(),
      })

      mixpanel.track(event.name, sanitizedProperties)

      if (this.config?.debug) {
        console.log('📊 Mixpanel track:', event)
      }
    } catch (error) {
      console.error('❌ Mixpanel track error:', error)
    }
  }

  /**
   * Identify a user
   */
  identify(userData: UserData): void {
    if (!this.isReady()) return

    try {
      mixpanel.identify(userData.id)

      if (userData.properties) {
        this.setUserProperties({
          ...userData.properties,
          identified_at: new Date().toISOString(),
        })
      }

      if (this.config?.debug) {
        console.log('👤 Mixpanel identify:', userData)
      }
    } catch (error) {
      console.error('❌ Mixpanel identify error:', error)
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return

    try {
      const sanitizedProperties = this.sanitizeProperties({
        ...properties,
        updated_at: new Date().toISOString(),
      })

      mixpanel.people.set(sanitizedProperties)

      if (this.config?.debug) {
        console.log('⚙️ Mixpanel user properties:', properties)
      }
    } catch (error) {
      console.error('❌ Mixpanel set user properties error:', error)
    }
  }

  /**
   * Set super properties (sent with every event)
   */
  setSuperProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return

    try {
      const sanitizedProperties = this.sanitizeProperties(properties)
      mixpanel.register(sanitizedProperties)

      if (this.config?.debug) {
        console.log('🌟 Mixpanel super properties:', properties)
      }
    } catch (error) {
      console.error('❌ Mixpanel set super properties error:', error)
    }
  }

  /**
   * Set super properties once (only if not already set)
   */
  setSuperPropertiesOnce(properties: Record<string, any>): void {
    if (!this.isReady()) return

    try {
      const sanitizedProperties = this.sanitizeProperties(properties)
      mixpanel.register_once(sanitizedProperties)

      if (this.config?.debug) {
        console.log('⭐ Mixpanel super properties once:', properties)
      }
    } catch (error) {
      console.error('❌ Mixpanel set super properties once error:', error)
    }
  }

  /**
   * Start timing an event
   */
  startTiming(eventName: string): void {
    if (!this.isReady()) return

    try {
      this.timingEvents.set(eventName, Date.now())

      if (this.config?.debug) {
        console.log('⏱️ Mixpanel start timing:', eventName)
      }
    } catch (error) {
      console.error('❌ Mixpanel start timing error:', error)
    }
  }

  /**
   * End timing an event and track it
   */
  endTiming(
    eventName: string,
    properties?: Record<string, any>
  ): number | null {
    if (!this.isReady()) return null

    try {
      const startTime = this.timingEvents.get(eventName)
      if (!startTime) {
        console.warn(`No start time found for: ${eventName}`)
        return null
      }

      const duration = Date.now() - startTime
      this.timingEvents.delete(eventName)

      // Track the timed event
      this.track({
        name: eventName,
        properties: {
          ...properties,
          duration_ms: duration,
          duration_seconds: Math.round((duration / 1000) * 100) / 100,
        },
      })

      if (this.config?.debug) {
        console.log('⏹️ Mixpanel end timing:', eventName, `${duration}ms`)
      }

      return duration
    } catch (error) {
      console.error('❌ Mixpanel end timing error:', error)
      return null
    }
  }

  /**
   * Track funnel step
   */
  trackFunnelStep(
    stepName: string,
    funnelName: string,
    properties?: Record<string, any>
  ): void {
    if (!this.isReady()) return

    try {
      this.track({
        name: `funnel_${stepName}`,
        properties: {
          ...properties,
          funnel_name: funnelName,
          funnel_step: stepName,
          funnel_timestamp: Date.now(),
        },
      })

      if (this.config?.debug) {
        console.log('🎯 Mixpanel funnel step:', funnelName, '->', stepName)
      }
    } catch (error) {
      console.error('❌ Mixpanel track funnel step error:', error)
    }
  }

  /**
   * Increment a user property
   */
  increment(property: string, value: number = 1): void {
    if (!this.isReady()) return

    try {
      mixpanel.people.increment(property, value)

      if (this.config?.debug) {
        console.log('➕ Mixpanel increment:', property, 'by', value)
      }
    } catch (error) {
      console.error('❌ Mixpanel increment error:', error)
    }
  }

  /**
   * Create alias for user - links anonymous device_id to authenticated user_id
   * IMPORTANT: Only call this ONCE per user, typically on first signup/login
   *
   * If a device ID was stored from main platform, it will be used for the alias.
   * Otherwise, uses the current Mixpanel distinct ID.
   */
  alias(userId: string): void {
    if (!this.isReady()) return

    try {
      const currentDistinctId = this.getDistinctId()
      const deviceIdToUse = this.storedDeviceId || currentDistinctId

      if (this.config?.debug) {
        console.log('🔗 Mixpanel alias - linking identities:', {
          from_anonymous_id: deviceIdToUse,
          to_user_id: userId,
          source: this.storedDeviceId ? 'main_platform' : 'auth_platform',
          current_distinct_id: currentDistinctId,
        })
      }

      // If we have a stored device ID from main platform and it differs from current,
      // we need to handle this specially. Mixpanel's alias() uses the current distinct_id,
      // so if the device IDs differ, we should log a warning but proceed with alias.
      // The cross-subdomain cookie should handle the identity linking automatically.
      if (this.storedDeviceId && this.storedDeviceId !== currentDistinctId) {
        console.warn(
          '[MIXPANEL] Device ID mismatch - stored:',
          this.storedDeviceId,
          'current:',
          currentDistinctId,
          '- proceeding with alias using current distinct ID'
        )
      }

      mixpanel.alias(userId)
    } catch (error) {
      console.error('❌ Mixpanel alias error:', error)
    }
  }

  /**
   * Get current user's distinct ID
   */
  getDistinctId(): string | null {
    if (!this.isReady()) return null

    try {
      return mixpanel.get_distinct_id()
    } catch (error) {
      console.error('❌ Mixpanel get distinct ID error:', error)
      return null
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string, properties?: Record<string, any>): void {
    if (!this.isReady()) return

    try {
      mixpanel.track('page_view', {
        page: path,
        referrer: document.referrer || 'direct',
        ...properties,
        timestamp: new Date().toISOString(),
      })

      if (this.config?.debug) {
        console.log('📄 Mixpanel page view:', path)
      }
    } catch (error) {
      console.error('❌ Mixpanel page view error:', error)
    }
  }

  /**
   * Track error/exception
   */
  captureException(error: Error, context?: Record<string, any>): void {
    if (!this.isReady()) return

    try {
      mixpanel.track('error', {
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
        ...context,
        timestamp: new Date().toISOString(),
      })

      if (this.config?.debug) {
        console.log('🚨 Mixpanel exception tracked:', error.message)
      }
    } catch (captureError) {
      console.error('❌ Mixpanel capture exception error:', captureError)
    }
  }

  /**
   * Reset user identification
   */
  reset(): void {
    if (!this.isReady()) return

    try {
      // Track logout event before reset
      mixpanel.track('user_logout', {
        timestamp: new Date().toISOString(),
      })

      // Reset user identification
      mixpanel.reset()

      if (this.config?.debug) {
        console.log('🔄 Mixpanel reset')
      }
    } catch (error) {
      console.error('❌ Mixpanel reset error:', error)
    }
  }

  /**
   * Check if Mixpanel is ready to use
   */
  isReady(): boolean {
    return this.isInitialized && this.config?.enabled === true
  }

  /**
   * Sanitize properties to ensure they're valid for Mixpanel
   */
  private sanitizeProperties(
    properties?: Record<string, any>
  ): Record<string, any> {
    if (!properties) return {}

    const sanitized: Record<string, any> = {}

    for (const [key, value] of Object.entries(properties)) {
      // Skip null/undefined values
      if (value === null || value === undefined) continue

      // Handle nested objects by stringifying them
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = JSON.stringify(value)
      } else {
        sanitized[key] = value
      }
    }

    return sanitized
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService()
